/**
 * Accessibility Scan page — simple, trustworthy scan experience.
 *
 * Primary flow: "Run scan" → POST /pipeline/run → poll /pipeline/latest.
 * Shows: last scan summary, one primary button, progress while running,
 * completion state with next actions. No pipeline internals in default view.
 */

import { useRef, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { triggerPipelineRun, cancelPipelineRun } from "../api/pipeline.js";
import { useScore } from "../hooks/use-score.js";
import { useLatestReport, useReportPayload } from "../hooks/use-report.js";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useBillingSummary } from "../hooks/use-billing.js";
import { useWebsites } from "../hooks/use-websites.js";
import { usePostScanRefresh } from "../hooks/use-post-scan-refresh.js";
import { useFeatureReadiness } from "../hooks/use-feature-readiness.js";
import { Breadcrumbs } from "../components/breadcrumbs.js";
import { ScoreCard } from "../components/score-card.js";
import { ScanProgress } from "../components/scan-progress.js";
import { ScanDiagnosticsPanel } from "../components/scan-diagnostics-panel.js";
import { buildDiagnosticSummary, translateDegradedFeatures } from "../lib/diagnostics.js";
import type { ScanOutcomeSummary, ScanVerdict } from "../api/product-types.js";

/* ── Helpers ────────────────────────────────────────────────────────────────── */

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString("en-IE");
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export function PipelinePage() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const qc = useQueryClient();

  // ── Data ─────────────────────────────────────────────────────────────────────
  const { data: score } = useScore(websiteId);

  const org = useFirstOrg();
  const { data: summaryData, isLoading: billingLoading } = useBillingSummary(org?.id);
  const limits = summaryData?.summary?.limits;
  const { data: websitesData } = useWebsites(org?.id);
  const website =
    websitesData?.websites.find((w) => w.id === websiteId) ?? null;

  const { data: reportSummary } = useLatestReport(websiteId);
  const { data: reportPayload } = useReportPayload(websiteId, reportSummary?.id);
  const truthfulness = reportPayload?.truthfulness ?? null;

  const { execution, isRunning, startFastPoll } = usePostScanRefresh(websiteId, org?.id);

  // Feature readiness + diagnostics
  const { data: features } = useFeatureReadiness();
  const degradedFeatures = features ? translateDegradedFeatures(features) : [];

  const isComplete =
    execution?.status === "success" || execution?.status === "partial";
  const isFailed = execution?.status === "failed";
  const hasNeverScanned = !execution;

  // ── Billing limit check ───────────────────────────────────────────────────
  // We check the summary usage to see if scan limit is reached.
  const usage = summaryData?.summary?.usage;
  const maxScansTotal = limits
    ? limits.scansPerMonthPerWebsite * limits.maxWebsites
    : Infinity;
  const scansRemaining =
    usage != null ? Math.max(0, maxScansTotal - usage.scansUsed) : Infinity;
  const scanLimitReached = scansRemaining <= 0;

  // ── Scan trigger ────────────────────────────────────────────────────────────
  // justTriggered bridges the gap between mutation success and the first
  // polling update (up to 5s). Without it, the page looks idle after clicking.
  // MUST be state (not a ref) so React re-renders when it changes and effects
  // that depend on it fire correctly (e.g. the 30s safety-net timer).
  const [justTriggered, setJustTriggered] = useState(false);
  // Track the execution ID returned by the trigger mutation so we can clear
  // justTriggered as soon as the poll returns THAT execution (any status).
  const triggeredExecId = useRef<string | null>(null);

  const runMutation = useMutation({
    mutationFn: () => triggerPipelineRun(websiteId!),
    onSuccess: (data) => {
      setJustTriggered(true);
      triggeredExecId.current = data.executionId;
      startFastPoll();
      void qc.invalidateQueries({ queryKey: ["pipeline-latest", websiteId] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelPipelineRun(websiteId!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pipeline-latest", websiteId] });
      runMutation.reset();
    },
  });

  // Clear justTriggered once the poll returns the triggered execution (any
  // status — running, failed, partial, success). This fixes the case where
  // the pipeline fails before the poll ever sees "running".
  useEffect(() => {
    if (!justTriggered || !execution) return;
    if (execution.id === triggeredExecId.current || isRunning) {
      setJustTriggered(false);
      triggeredExecId.current = null;
    }
  }, [justTriggered, execution, isRunning]);

  const showAsRunning = isRunning || runMutation.isPending || justTriggered;

  // Safety net: auto-clear justTriggered after 30s so the UI never gets
  // permanently stuck on "Preparing scan" if the poll fails to detect the
  // new execution (e.g. API errors, network issues).
  useEffect(() => {
    if (!justTriggered) return;
    const timer = setTimeout(() => {
      setJustTriggered(false);
      triggeredExecId.current = null;
      void qc.invalidateQueries({ queryKey: ["pipeline-latest", websiteId] });
    }, 30_000);
    return () => clearTimeout(timer);
  }, [justTriggered, qc, websiteId]);

  // Derive actionable error message from mutation error
  const mutationError = runMutation.error instanceof Error ? runMutation.error : null;
  const isAlreadyRunning = mutationError?.message?.includes("already running") || false;
  const isBillingBlocked = mutationError?.message?.includes("limit") || mutationError?.message?.includes("quota") || false;

  // Compute disabled state
  const canRun = !showAsRunning && !scanLimitReached && !billingLoading;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="product-page">
      <Breadcrumbs
        items={[
          { label: "Websites", to: "/websites" },
          { label: website?.domain ?? "Website", to: `/websites/${websiteId}` },
          { label: "Scan" },
        ]}
      />
      <h1 className="page-title">Accessibility scan</h1>
      <p className="page-subtitle">
        Run a scan to check your website for accessibility issues.
      </p>

      {/* ── Last scan summary ──────────────────────────────────────────── */}
      {!showAsRunning && score && (
        <section
          className="product-section"
          aria-labelledby="scan-summary-heading"
        >
          <h2 id="scan-summary-heading" className="section-title">
            Last scan
          </h2>
          <div className="scan-summary">
            <div className="scan-summary__score">
              <ScoreCard
                scoreValue={score.scoreValue}
                scoreBand={score.scoreBand}
                confidence={score.confidence}
                label="Current score"
              />
            </div>
            {execution && (
              <div className="scan-summary__details">
                <div className="stat-grid">
                  <div className="stat-item">
                    <span className="stat-item__value">
                      {score.issueGroupCount}
                    </span>
                    <span className="stat-item__label">Issues found</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-item__value">
                      {formatTimeAgo(
                        execution.completedAt ?? execution.startedAt,
                      )}
                    </span>
                    <span className="stat-item__label">Completed</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Primary action ─────────────────────────────────────────────── */}
      {!showAsRunning && (
        <section
          className="product-section"
          aria-labelledby="scan-action-heading"
        >
          {hasNeverScanned ? (
            <>
              <h2 id="scan-action-heading" className="section-title">
                Get started
              </h2>
              <p className="text-muted mb-3">
                No scan has been run yet. Start your first accessibility scan to
                discover issues on your website.
              </p>
            </>
          ) : (
            <h2 id="scan-action-heading" className="section-title">
              Run a new scan
            </h2>
          )}

          <div className="scan-controls">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void runMutation.mutate()}
              disabled={!canRun}
              aria-busy={runMutation.isPending}
            >
              {runMutation.isPending
                ? "Starting scan…"
                : billingLoading
                  ? "Loading…"
                  : hasNeverScanned
                    ? "Run your first scan"
                    : "Run scan"}
            </button>
          </div>

          {/* Scan limit warning */}
          {scanLimitReached && (
            <div
              className="alert alert--warning mt-3"
              role="alert"
            >
              You have reached your scan limit for this month.
              {limits && (
                <span>
                  {" "}
                  Your plan allows {maxScansTotal}{" "}
                  {maxScansTotal === 1 ? "scan" : "scans"} per month.
                </span>
              )}
              <Link
                to="/billing"
                className="btn btn--secondary btn--sm"
                style={{ marginLeft: "12px" }}
              >
                Upgrade
              </Link>
            </div>
          )}

          {/* Mutation errors — contextual messages */}
          {mutationError && (
            <div
              className="alert alert--error mt-3"
              role="alert"
            >
              {isAlreadyRunning
                ? "A scan is already running for this website. Please wait for it to finish, or cancel it below."
                : isBillingBlocked
                  ? "Your scan limit has been reached. Upgrade your plan to run more scans."
                  : `Scan could not be started. ${mutationError.message}`}
            </div>
          )}

          {/* Cancel stuck scan */}
          {isAlreadyRunning && (
            <button
              type="button"
              className="btn btn--secondary mt-2"
              onClick={() => void cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              aria-busy={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? "Cancelling…" : "Cancel stuck scan"}
            </button>
          )}

          {!hasNeverScanned && !mutationError && (
            <p className="text-muted text-sm mt-2">
              The scan runs in the background. You can leave this page and come
              back — results will be saved automatically.
            </p>
          )}
        </section>
      )}

      {/* ── Queued / just-started state ──────────────────────────────────── */}
      {/* Bridges the gap between clicking "Run scan" and the first poll update */}
      {!isRunning && justTriggered && (
        <section className="product-section">
          <div className="scan-progress">
            <div className="scan-progress__header">
              <h3 className="scan-progress__title">
                Scan queued
              </h3>
            </div>
            <div
              className="scan-progress__track"
              role="progressbar"
              aria-valuenow={2}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Scan progress: starting"
            >
              <div className="scan-progress__fill" style={{ width: "2%" }} />
            </div>
            <div className="scan-progress__phase">
              <span className="scan-progress__phase-label">Preparing scan</span>
              <span className="scan-progress__phase-desc text-muted text-sm">
                Your scan has been queued. Discovery and page grouping will begin shortly.
              </span>
            </div>
          </div>
        </section>
      )}

      {/* ── Progress (when running) ────────────────────────────────────── */}
      {isRunning && execution && (
        <section
          className="product-section"
          aria-labelledby="scan-progress-heading"
        >
          <ScanProgress execution={execution} />
        </section>
      )}

      {/* ── Inflight state (mutation pending, before queue confirms) ──── */}
      {runMutation.isPending && !isRunning && (
        <section className="product-section">
          <div className="scan-progress">
            <div className="scan-progress__header">
              <h3 className="scan-progress__title">
                Starting scan…
              </h3>
            </div>
            <div
              className="scan-progress__track"
              role="progressbar"
              aria-valuenow={1}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Scan progress: starting"
            >
              <div className="scan-progress__fill" style={{ width: "1%" }} />
            </div>
            <div className="scan-progress__phase">
              <span className="scan-progress__phase-desc text-muted text-sm">
                Queuing your scan. This usually takes a moment.
              </span>
            </div>
          </div>
        </section>
      )}

      {/* ── Completion state ───────────────────────────────────────────── */}
      {!isRunning && isComplete && (
        <section
          className="product-section"
          aria-labelledby="scan-next-heading"
        >
          <h2 id="scan-next-heading" className="section-title">
            What to do next
          </h2>
          <p className="text-muted mb-3">
            {execution?.status === "partial" ? (
              <>
                Your scan completed but not all pages could be tested.
                {score && score.pagesScanned > 0 && (
                  <> {score.pagesScanned} {score.pagesScanned === 1 ? "page was" : "pages were"} scanned successfully.</>
                )}
                {" "}A report is available, but results may not cover your full site.
              </>
            ) : (
              <>
                Your scan completed successfully
                {score && score.pagesScanned > 0 && (
                  <> — {score.pagesScanned} {score.pagesScanned === 1 ? "page" : "pages"} tested, {score.issueGroupCount} {score.issueGroupCount === 1 ? "issue" : "issues"} found</>
                )}
                . Review the results to prioritise fixes.
              </>
            )}
          </p>
          <div className="action-links">
            <Link
              to={`/websites/${websiteId}/report`}
              className="btn btn--primary"
            >
              View report
            </Link>
            <Link
              to={`/websites/${websiteId}`}
              className="btn btn--secondary"
            >
              View dashboard
            </Link>
          </div>
        </section>
      )}

      {/* ── Failed state (not running) ─────────────────────────────────── */}
      {!isRunning && isFailed && execution && (
        <section className="product-section">
          <ScanProgress execution={execution} />
        </section>
      )}

      {/* ── Scan outcome summary (post-completion) ──────────────────────── */}
      {!isRunning && !hasNeverScanned && reportPayload?.scanOutcome && (
        <ScanOutcomePanel outcome={reportPayload.scanOutcome} />
      )}

      {/* ── Feature readiness diagnostics (post-completion) ──────────────── */}
      {!isRunning && !hasNeverScanned && reportPayload?.scanOutcome && degradedFeatures.length > 0 && (
        <section className="product-section" aria-labelledby="readiness-diagnostics-heading">
          <h2 id="readiness-diagnostics-heading" className="section-title">
            Feature notices
          </h2>
          <ScanDiagnosticsPanel
            summary={buildDiagnosticSummary(
              reportPayload.scanOutcome,
              (reportPayload.truthfulness?.screenshotsCaptured ?? 0) > 0,
              degradedFeatures,
            )}
            variant="full"
          />
        </section>
      )}

      {/* ── Coverage diagnostics (post-completion) ──────────────────────── */}
      {!isRunning &&
        !hasNeverScanned &&
        truthfulness &&
        truthfulness.pagesScanned < truthfulness.pagesDiscovered &&
        truthfulness.pagesDiscovered > 0 && (
        <section
          className="product-section"
          aria-labelledby="scan-diagnostics-heading"
        >
          <h2 id="scan-diagnostics-heading" className="section-title">
            Why only {truthfulness.pagesScanned} of{" "}
            {truthfulness.pagesDiscovered} pages were scanned
          </h2>

          {truthfulness.pagesNotAttempted > 0 &&
            truthfulness.pagesBlocked === 0 &&
            truthfulness.pagesFailed === 0 && (
            <p className="text-muted text-sm">
              The scanner tested representative pages from each of the{" "}
              {truthfulness.templatesDetected} template groups detected on
              your site, rather than every discovered URL. This is expected
              and provides broad coverage efficiently.
            </p>
          )}

          {(truthfulness.pagesBlocked > 0 || truthfulness.pagesFailed > 0) && (
            <ul className="diagnostics-list">
              {truthfulness.pagesNotAttempted > 0 && (
                <li className="diagnostics-list__item">
                  <strong>{truthfulness.pagesNotAttempted} not tested</strong>
                  {" "}&mdash; representative sampling from template groups.
                </li>
              )}
              {truthfulness.pagesBlocked > 0 && (
                <li className="diagnostics-list__item diagnostics-list__item--warning">
                  <strong>{truthfulness.pagesBlocked} blocked</strong>
                  {" "}by {truthfulness.wafVendorDetected === "Cloudflare"
                    ? "Cloudflare security challenge"
                    : "site security (WAF, bot challenge, or login wall)"}.
                </li>
              )}
              {truthfulness.pagesFailed > 0 && (
                <li className="diagnostics-list__item diagnostics-list__item--danger">
                  <strong>{truthfulness.pagesFailed} failed to load</strong>
                  {" "}&mdash; errors, timeouts, or empty content.
                </li>
              )}
            </ul>
          )}

          {/* ── Cloudflare-specific allowlisting guidance ──────────────── */}
          {truthfulness.pagesBlocked > 0 &&
            truthfulness.wafVendorDetected === "Cloudflare" && (
            <div className="alert alert--info mt-3">
              <strong>Cloudflare is blocking the scanner.</strong> To fix this,
              add a WAF exception in your Cloudflare dashboard:
              <ol style={{ margin: "8px 0 4px 16px", lineHeight: "1.6" }}>
                <li>
                  Go to <strong>Security → WAF → Custom rules</strong> in Cloudflare.
                </li>
                <li>
                  Create an exception rule: <em>When User-Agent contains "Chrome"
                  AND IP address is in {"{your server IP}"}</em> → <strong>Skip</strong> all
                  managed rules.
                </li>
                <li>
                  Or set <strong>Security Level</strong> to "Essentially Off" for
                  your scanner's IP range under <strong>Security → Settings</strong>.
                </li>
              </ol>
              <p className="text-sm" style={{ margin: "4px 0 0 0" }}>
                See{" "}
                <Link to={`/websites/${websiteId}/settings`}>
                  Scanner access in Settings
                </Link>{" "}
                for the scanner's user-agent and details.
              </p>
            </div>
          )}

          {/* ── Generic WAF guidance (non-Cloudflare) ──────────────────── */}
          {truthfulness.pagesBlocked > 0 &&
            truthfulness.wafVendorDetected !== "Cloudflare" &&
            truthfulness.wafVendorDetected != null && (
            <div className="alert alert--info mt-3">
              <strong>Your firewall is blocking the scanner.</strong> You may
              need to allowlist the scanner's user-agent or IP address in your{" "}
              {truthfulness.wafVendorDetected} settings.{" "}
              <Link to={`/websites/${websiteId}/settings`}>
                View scanner details in Settings
              </Link>.
            </div>
          )}

          <p className="text-muted text-sm mt-2">
            View the{" "}
            <Link to={`/websites/${websiteId}/report`}>full report</Link>{" "}
            for detailed coverage and diagnostics.
          </p>
        </section>
      )}

      {/* ── Current scan setup ────────────────────────────────────────── */}
      {website && (
        <section
          className="product-section"
          aria-labelledby="scan-setup-heading"
        >
          <h2 id="scan-setup-heading" className="section-title">
            Current scan setup
          </h2>
          <div className="stat-grid">
            <div className="stat-item">
              <span className="stat-item__value">{website.scanDepth}</span>
              <span className="stat-item__label">Max pages per scan</span>
            </div>
            <div className="stat-item">
              <span className="stat-item__value" style={{ textTransform: "capitalize" }}>
                {website.scanFrequency === "manual"
                  ? "Manual only"
                  : website.scanFrequency}
              </span>
              <span className="stat-item__label">Scan frequency</span>
            </div>
            {limits && (
              <div className="stat-item">
                <span className="stat-item__value">
                  {!limits.screenshotsEnabled
                    ? "No"
                    : features?.screenshots?.status === "ready"
                      ? "Yes"
                      : "Plan only"}
                </span>
                <span className="stat-item__label">Visual evidence</span>
              </div>
            )}
          </div>
          <p className="text-muted text-sm mt-2">
            <Link to={`/websites/${websiteId}/settings`}>
              Change scan settings
            </Link>
          </p>
        </section>
      )}

      {/* ── How this scan works ──────────────────────────────────────── */}
      <section className="product-section" aria-labelledby="methodology-heading">
        <details>
          <summary
            id="methodology-heading"
            className="section-title cursor-pointer"
          >
            How this scan works
          </summary>
          <div className="mt-3">
            <p className="text-sm mb-2">
              <strong>Tools:</strong> Axe-core, PA11y, and custom checks run
              in a headless Chromium browser against your live site.
            </p>
            <p className="text-sm mb-2">
              <strong>Page selection:</strong> Pages are discovered
              automatically (sitemap, crawling, search console). Similar pages
              are grouped by template, and representative pages from each group
              are tested. This gives broad coverage without scanning every
              individual page.
            </p>
            <p className="text-sm mb-2">
              <strong>What is not covered:</strong> Automated scans cannot
              assess content quality, keyboard-only workflows, screen reader
              compatibility, or cognitive accessibility. Manual review is
              required for full WCAG compliance.
            </p>
            <p className="text-sm text-muted">
              <strong>Blocked pages?</strong> If your site uses a WAF or bot
              protection, you may need to allowlist the scanner. See{" "}
              <Link to={`/websites/${websiteId}/settings`}>
                Scanner access in Settings
              </Link>
              {" "}for allowlisting details.
            </p>
          </div>
        </details>
      </section>
    </div>
  );
}

/* ── Scan outcome panel ────────────────────────────────────────────────────── */

function verdictLabel(v: ScanVerdict): string {
  switch (v) {
    case "clean":
      return "No issues found";
    case "issues_found":
      return "Issues detected";
    case "partial_data":
      return "Partial scan";
    case "blocked":
      return "Blocked by site security";
    case "no_data":
      return "No data";
  }
}

function verdictClass(v: ScanVerdict): string {
  switch (v) {
    case "clean":
      return "text-success";
    case "issues_found":
      return "text-danger";
    case "partial_data":
      return "text-warning";
    case "blocked":
      return "text-danger";
    case "no_data":
      return "text-muted";
  }
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function ScanOutcomePanel({ outcome }: { outcome: ScanOutcomeSummary }) {
  return (
    <section className="product-section" aria-labelledby="scan-outcome-heading">
      <h2 id="scan-outcome-heading" className="section-title">
        Scan outcome
        <span
          className={`text-sm ${verdictClass(outcome.verdict)} fw-normal`}
          style={{ marginLeft: "0.5rem" }}
        >
          {verdictLabel(outcome.verdict)}
        </span>
      </h2>

      <div className="stat-grid">
        <div className="stat-item">
          <span className="stat-item__value">
            {outcome.pagesScanned}
            <span className="text-muted text-sm fw-normal">
              {" "}/ {outcome.pagesDiscovered}
            </span>
          </span>
          <span className="stat-item__label">Pages scanned</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__value">{outcome.issueGroupCount}</span>
          <span className="stat-item__label">Issue groups</span>
        </div>
        <div className="stat-item">
          <span className="stat-item__value">{outcome.coveragePercent}%</span>
          <span className="stat-item__label">Coverage</span>
        </div>
        {outcome.durationSec != null && (
          <div className="stat-item">
            <span className="stat-item__value">
              {formatDuration(outcome.durationSec)}
            </span>
            <span className="stat-item__label">Duration</span>
          </div>
        )}
      </div>

      {/* Issue severity breakdown (only when issues exist) */}
      {outcome.issueGroupCount > 0 && (
        <div className="stat-grid mt-2">
          {outcome.criticalCount > 0 && (
            <div className="stat-item stat-item--danger">
              <span className="stat-item__value">{outcome.criticalCount}</span>
              <span className="stat-item__label">Critical</span>
            </div>
          )}
          {outcome.seriousCount > 0 && (
            <div className="stat-item stat-item--warning">
              <span className="stat-item__value">{outcome.seriousCount}</span>
              <span className="stat-item__label">Serious</span>
            </div>
          )}
          <div className="stat-item">
            <span className="stat-item__value">{outcome.moderateCount}</span>
            <span className="stat-item__label">Moderate</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">{outcome.minorCount}</span>
            <span className="stat-item__label">Minor</span>
          </div>
        </div>
      )}

      {/* Page funnel detail (only when pages were lost) */}
      {(outcome.pagesBlocked > 0 || outcome.pagesFailed > 0) && (
        <div className="text-sm text-muted mt-2">
          {outcome.pagesBlocked > 0 && (
            <span>{outcome.pagesBlocked} blocked by site security. </span>
          )}
          {outcome.pagesFailed > 0 && (
            <span>{outcome.pagesFailed} failed to load. </span>
          )}
          {outcome.pagesNotSelected > 0 && (
            <span>
              {outcome.pagesNotSelected} not tested (template sampling).
            </span>
          )}
        </div>
      )}

      {outcome.pageCapApplied && (
        <p className="text-sm text-warning mt-1">
          Your plan's page limit reduced the number of pages scanned.
        </p>
      )}
    </section>
  );
}
